/**
 * Product Compatibility Checker
 * Checks if customer's model number matches with product tags
 */

(function() {
  'use strict';

  console.log('🔍 Product Compatibility Checker: Initializing...');

  /**
   * Initialize compatibility checker for a specific block
   */
  function initCompatibilityChecker(blockId) {
    const input = document.getElementById(`compatibility-model-input-${blockId}`);
    const button = document.getElementById(`compatibility-check-button-${blockId}`);
    const tagsData = document.getElementById(`compatibility-data-${blockId}`);

    if (!input || !button || !tagsData) {
      console.warn('🔍 Compatibility Checker: Required elements not found for block', blockId);
      return;
    }

    // Get the product tags
    const productTagsString = tagsData.getAttribute('data-product-tags') || '';
    console.log('🔍 Compatibility Checker: Product tags:', productTagsString);

    // Parse product tags
    const productTags = parseProductTags(productTagsString);
    console.log('🔍 Compatibility Checker: Parsed tags:', productTags);

    // Add click event to button
    button.addEventListener('click', function(event) {
      // Prevent form submission
      event.preventDefault();
      event.stopPropagation();
      
      const userModelNumber = input.value.trim().toUpperCase();
      
      if (!userModelNumber) {
        showMessage('Please enter a model number', 'error');
        return;
      }

      checkCompatibility(userModelNumber, productTags);
    });

    // Add enter key support
    input.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        button.click();
      }
    });
  }

  /**
   * Parse product tags - filter and extract compatible model tags
   */
  function parseProductTags(productTagsString) {
    console.log('🔍 Parsing product tags:', productTagsString);
    
    if (!productTagsString) {
      console.log('🔍 No product tags provided');
      return [];
    }

    // Split by pipe character (|) as we joined tags with it
    const allTags = productTagsString.split('|').map(tag => tag.trim()).filter(tag => tag.length > 0);
    console.log('🔍 All tags:', allTags);
    
    // Filter only tags that start with "compatible_model_"
    const compatibleModelTags = allTags.filter(tag => tag.toLowerCase().startsWith('compatible_model_'));
    console.log('🔍 Compatible model tags:', compatibleModelTags);
    
    console.log('🔍 Final parsed tags:', compatibleModelTags);
    return compatibleModelTags;
  }
  
  /**
   * Extract model number from tag (remove "compatible_model_" prefix)
   */
  function extractModelNumber(tag) {
    if (tag.toLowerCase().startsWith('compatible_model_')) {
      return tag.substring('compatible_model_'.length).trim();
    }
    return tag;
  }

  /**
   * Check if user's model number is compatible with product tags
   */
  function checkCompatibility(userModelNumber, productTags) {
    console.log('🔍 Checking compatibility for:', userModelNumber);
    console.log('🔍 Available product tags:', productTags);
    
    // Check for matches in tags
    let isCompatible = false;
    let matchedTags = [];

    for (let tag of productTags) {
      // Extract the model number from the tag (removes "compatible_model_" prefix)
      const modelNumber = extractModelNumber(tag);
      console.log(`🔍 Checking tag: "${tag}" (model: "${modelNumber}") against user input: "${userModelNumber}"`);
      
      // Exact match (case insensitive)
      if (modelNumber.toUpperCase() === userModelNumber.toUpperCase()) {
        console.log('🔍 Exact match found!');
        isCompatible = true;
        matchedTags.push(tag);
        continue;
      }
      
      // Partial match (user input contains the model or vice versa)
      if (modelNumber.toUpperCase().includes(userModelNumber.toUpperCase()) || 
          userModelNumber.toUpperCase().includes(modelNumber.toUpperCase())) {
        console.log('🔍 Partial match found!');
        isCompatible = true;
        matchedTags.push(tag);
        continue;
      }

      // Fuzzy match - remove common separators and check
      const cleanUserModel = userModelNumber.replace(/[-_\s\.]/g, '').toUpperCase();
      const cleanModelNumber = modelNumber.replace(/[-_\s\.]/g, '').toUpperCase();
      
      console.log(`🔍 Clean comparison: "${cleanModelNumber}" vs "${cleanUserModel}"`);
      
      if (cleanModelNumber === cleanUserModel || 
          cleanModelNumber.includes(cleanUserModel) || 
          cleanUserModel.includes(cleanModelNumber)) {
        console.log('🔍 Fuzzy match found!');
        isCompatible = true;
        matchedTags.push(tag);
        continue;
      }
    }

    if (isCompatible) {
      console.log('🔍 RESULT: Compatible! Matched tags:', matchedTags);
      showCompatibilityPopup(true, userModelNumber, matchedTags, productTags);
    } else {
      console.log('🔍 RESULT: Not compatible with current product');
      console.log('🔍 Searching all products for matching model number...');
      // Search all products for this model number
      searchAllProductsForModel(userModelNumber, productTags);
    }
  }

  /**
   * Search all products in the store for matching model number
   */
  function searchAllProductsForModel(userModelNumber, currentProductTags) {
    console.log('🔍 Searching all products for model:', userModelNumber);
    
    // Show loading state
    showSearchingPopup(userModelNumber);
    
    // Search for products using Shopify's search API
    const searchTag = `compatible_model_${userModelNumber}`;
    const searchUrl = `/search?q=tag:${encodeURIComponent(searchTag)}&type=product&view=json`;
    
    console.log('🔍 Search URL:', searchUrl);
    
    // Try to fetch products via search API
    fetch(searchUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error('Search failed');
        }
        return response.text();
      })
      .then(html => {
        // Parse the HTML response to extract products
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Try to find product items in the response
        const productItems = doc.querySelectorAll('.product-item, [data-product-id]');
        
        if (productItems.length > 0) {
          console.log('🔍 Found matching products:', productItems.length);
          const products = extractProductsFromHTML(productItems);
          showCompatibilityPopupWithAlternatives(false, userModelNumber, [], currentProductTags, products);
        } else {
          // Fallback: Try alternative search method
          searchProductsAlternative(userModelNumber, currentProductTags);
        }
      })
      .catch(error => {
        console.error('🔍 Error searching products:', error);
        // Fallback: Try alternative search method
        searchProductsAlternative(userModelNumber, currentProductTags);
      });
  }

  /**
   * Alternative search method using Shopify's products.json endpoint with pagination
   */
  function searchProductsAlternative(userModelNumber, currentProductTags) {
    console.log('🔍 Using alternative search method with pagination...');
    
    const matchingProducts = [];
    let allProducts = [];
    
    // Function to fetch products page by page
    function fetchProductsPage(page = 1) {
      console.log(`🔍 Fetching products page ${page}...`);
      
      return fetch(`/products.json?limit=250&page=${page}`)
        .then(response => response.json())
        .then(data => {
          const products = data.products || [];
          console.log(`🔍 Page ${page}: Fetched ${products.length} products`);
          
          if (products.length > 0) {
            allProducts = allProducts.concat(products);
            
            // If we got 250 products, there might be more pages
            if (products.length === 250) {
              return fetchProductsPage(page + 1);
            }
          }
          
          return allProducts;
        });
    }
    
    // Start fetching from page 1
    fetchProductsPage(1)
      .then(products => {
        console.log(`🔍 Total products fetched: ${products.length}`);
        
        // Check each product's tags for matching model number
        for (let product of products) {
          const productTags = product.tags || [];
          let productMatched = false;
          
          for (let tag of productTags) {
            let modelNumber = tag.trim();
            let matchedTag = tag;
            
            // Check if tag starts with "compatible_model_" prefix
            if (tag.toLowerCase().startsWith('compatible_model_')) {
              modelNumber = tag.substring('compatible_model_'.length).trim();
            }
            
            // Check if this tag matches the user's input
            if (isModelNumberMatch(userModelNumber, modelNumber)) {
              if (!productMatched) {
                matchingProducts.push({
                  id: product.id,
                  title: product.title,
                  handle: product.handle,
                  url: `/products/${product.handle}`,
                  image: product.images?.[0]?.src || product.image?.src || '',
                  price: product.variants?.[0]?.price || '',
                  matchedTag: matchedTag
                });
                productMatched = true;
                console.log('🔍 Match found!', {
                  product: product.title,
                  tag: matchedTag,
                  modelNumber: modelNumber,
                  userInput: userModelNumber
                });
              }
              break; // Only add product once even if multiple tags match
            }
          }
        }
        
        console.log('🔍 Found matching products:', matchingProducts.length);
        
        if (matchingProducts.length > 0) {
          showCompatibilityPopupWithAlternatives(false, userModelNumber, [], currentProductTags, matchingProducts);
        } else {
          // No matching products found anywhere
          showCompatibilityPopup(false, userModelNumber, [], currentProductTags);
        }
      })
      .catch(error => {
        console.error('🔍 Error fetching products:', error);
        // Fallback to regular not compatible message
        showCompatibilityPopup(false, userModelNumber, [], currentProductTags);
      });
  }

  /**
   * Check if a model number matches user input (using same logic as main check)
   */
  function isModelNumberMatch(userModelNumber, productModelNumber) {
    const userUpper = userModelNumber.toUpperCase();
    const productUpper = productModelNumber.toUpperCase();
    
    // Exact match
    if (userUpper === productUpper) {
      return true;
    }
    
    // Partial match
    if (userUpper.includes(productUpper) || productUpper.includes(userUpper)) {
      return true;
    }
    
    // Fuzzy match (remove separators)
    const cleanUser = userModelNumber.replace(/[-_\s\.]/g, '').toUpperCase();
    const cleanProduct = productModelNumber.replace(/[-_\s\.]/g, '').toUpperCase();
    
    if (cleanUser === cleanProduct || cleanUser.includes(cleanProduct) || cleanProduct.includes(cleanUser)) {
      return true;
    }
    
    return false;
  }

  /**
   * Extract product information from HTML elements
   */
  function extractProductsFromHTML(productItems) {
    const products = [];
    
    productItems.forEach(item => {
      const productId = item.getAttribute('data-product-id');
      const titleElement = item.querySelector('.product-title, .product-item__title, h3, h2');
      const linkElement = item.querySelector('a[href*="/products/"]');
      const imageElement = item.querySelector('img');
      const priceElement = item.querySelector('.price, .product-price, [data-price]');
      
      if (linkElement && titleElement) {
        products.push({
          id: productId || '',
          title: titleElement.textContent.trim(),
          url: linkElement.getAttribute('href'),
          image: imageElement ? imageElement.getAttribute('src') || imageElement.getAttribute('data-src') : '',
          price: priceElement ? priceElement.textContent.trim() : ''
        });
      }
    });
    
    return products;
  }

  /**
   * Show searching popup (loading state)
   */
  function showSearchingPopup(userModelNumber) {
    removeExistingPopup();
    
    const popupHTML = `
      <div class="compatibility-result-overlay show">
        <div class="compatibility-result-popup">
          <div class="compatibility-result-content">
            <div class="compatibility-searching">
              <div class="compatibility-searching-spinner"></div>
              <p class="compatibility-searching-text">Searching all products for model <strong>${userModelNumber}</strong>...</p>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    addPopupStyles();
    addSearchingStyles();
  }

  /**
   * Add searching animation styles
   */
  function addSearchingStyles() {
    if (document.getElementById('compatibility-searching-styles')) return;
    
    const styles = `
      <style id="compatibility-searching-styles">
        .compatibility-searching {
          padding: 20px;
          text-align: center;
        }
        
        .compatibility-searching-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #000a46;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .compatibility-searching-text {
          font-family: 'Avenir Next Medium', 'Avenir Next', Arial, sans-serif;
          font-size: 16px;
          color: #4b5563;
          margin: 0;
        }
      </style>
    `;
    
    document.head.insertAdjacentHTML('beforeend', styles);
  }

  /**
   * Show compatibility result popup
   */
  function showCompatibilityPopup(isCompatible, userModelNumber, matchedTags, allProductTags) {
    // Remove existing popup if any
    removeExistingPopup();

    const popupHTML = createPopupHTML(isCompatible, userModelNumber, matchedTags, allProductTags);
    
    // Add popup to DOM
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    
    // Add styles if not already added
    addPopupStyles();
    
    // Show popup with animation
    setTimeout(() => {
      const overlay = document.querySelector('.compatibility-result-overlay');
      if (overlay) {
        overlay.classList.add('show');
      }
    }, 10);
    
    // Add event listeners
    addPopupEventListeners();
  }

  /**
   * Show compatibility result popup with alternative products
   */
  function showCompatibilityPopupWithAlternatives(isCompatible, userModelNumber, matchedTags, allProductTags, alternativeProducts) {
    // Remove existing popup if any
    removeExistingPopup();

    const popupHTML = createPopupHTMLWithAlternatives(isCompatible, userModelNumber, matchedTags, allProductTags, alternativeProducts);
    
    // Add popup to DOM
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    
    // Add styles if not already added
    addPopupStyles();
    addAlternativeProductsStyles();
    
    // Show popup with animation
    setTimeout(() => {
      const overlay = document.querySelector('.compatibility-result-overlay');
      if (overlay) {
        overlay.classList.add('show');
      }
    }, 10);
    
    // Add event listeners
    addPopupEventListeners();
  }

  /**
   * Create popup HTML
   */
  function createPopupHTML(isCompatible, userModelNumber, matchedTags, allProductTags) {
    const iconSuccess = `
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#10B981"/>
        <path d="M14 24L20 30L34 16" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    const iconError = `
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#EF4444"/>
        <path d="M16 16L32 32M32 16L16 32" stroke="white" stroke-width="3" stroke-linecap="round"/>
      </svg>
    `;

    return `
      <div class="compatibility-result-overlay">
        <div class="compatibility-result-popup">
          <button type="button" class="compatibility-result-close" onclick="closeCompatibilityResult()">&times;</button>
          
          <div class="compatibility-result-content">
            <div class="compatibility-result-icon">
              ${isCompatible ? iconSuccess : iconError}
            </div>
            
            <h2 class="compatibility-result-title">
              ${isCompatible ? 'Great News! It\'s Compatible! ✓' : 'Not Compatible'}
            </h2>
            
            <p class="compatibility-result-message">
              ${isCompatible 
                ? `Your model <strong>${userModelNumber}</strong> is compatible with this product!`
                : `Unfortunately, your model <strong>${userModelNumber}</strong> does not match any compatible models.`
              }
            </p>
            
            ${allProductTags.length > 0 ? `
              <div class="compatibility-result-models">
                <h3 class="compatibility-result-models-title">
                  ${isCompatible ? 'All Compatible Models:' : 'These are the compatible models:'}
                </h3>
                <div class="compatibility-result-models-list">
                  ${allProductTags.map(tag => {
                    const isMatched = matchedTags.includes(tag);
                    const displayModel = extractModelNumber(tag);
                    return `
                      <span class="compatibility-result-model-tag ${isMatched ? 'matched' : ''}">
                        ${displayModel}
                      </span>
                    `;
                  }).join('')}
                </div>
              </div>
            ` : ''}
            
            ${isCompatible ? `
              <div class="compatibility-result-actions">
                <button type="button" class="compatibility-result-button compatibility-result-button--primary" onclick="scrollToAddToCart()">
                  Add to Basket
                </button>
                <button type="button" class="compatibility-result-button compatibility-result-button--secondary" onclick="closeCompatibilityResult()">
                  Continue Shopping
                </button>
              </div>
            ` : `
              <div class="compatibility-result-actions">
                <button type="button" class="compatibility-result-button compatibility-result-button--secondary" onclick="tryAgainCompatibility()">
                  Try Again
                </button>
                <button type="button" class="compatibility-result-button compatibility-result-button--secondary" onclick="closeCompatibilityResult()">
                  Close
                </button>
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Create popup HTML with alternative products
   */
  function createPopupHTMLWithAlternatives(isCompatible, userModelNumber, matchedTags, allProductTags, alternativeProducts) {
    const iconInfo = `
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#3B82F6"/>
        <path d="M24 16V24M24 32H24.02" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    return `
      <div class="compatibility-result-overlay">
        <div class="compatibility-result-popup compatibility-result-popup--large">
          <button type="button" class="compatibility-result-close" onclick="closeCompatibilityResult()">&times;</button>
          
          <div class="compatibility-result-content">
            <div class="compatibility-result-icon">
              ${iconInfo}
            </div>
            
            <h2 class="compatibility-result-title">
              Not Compatible with This Product
            </h2>
            
            <p class="compatibility-result-message">
              Your model <strong>${userModelNumber}</strong> is not compatible with the current product.<br>
              However, we found <strong>${alternativeProducts.length}</strong> compatible product${alternativeProducts.length > 1 ? 's' : ''} for your model:
            </p>
            
            ${allProductTags.length > 0 ? `
              <div class="compatibility-result-models">
                <h3 class="compatibility-result-models-title">
                  Compatible models for the this product:
                </h3>
                <div class="compatibility-result-models-list">
                  ${allProductTags.map(tag => {
                    const displayModel = extractModelNumber(tag);
                    return `
                      <span class="compatibility-result-model-tag">
                        ${displayModel}
                      </span>
                    `;
                  }).join('')}
                </div>
              </div>
            ` : ''}
            
            <div class="alternative-products-section">
              <h3 class="alternative-products-title">Compatible Products for Model ${userModelNumber}:</h3>
              
              <div class="alternative-products-grid">
                ${alternativeProducts.map(product => {
                  // Extract model number from matched tag (handles both with and without prefix)
                  let modelNumber = userModelNumber;
                  if (product.matchedTag) {
                    if (product.matchedTag.toLowerCase().startsWith('compatible_model_')) {
                      modelNumber = product.matchedTag.substring('compatible_model_'.length).trim();
                    } else {
                      modelNumber = product.matchedTag.trim();
                    }
                  }
                  const displayPrice = product.price ? `£${product.price}` : '';
                  
                  return `
                    <div class="alternative-product-card">
                      ${product.image ? `
                        <div class="alternative-product-image">
                          <img src="${product.image}" alt="${product.title}" loading="lazy">
                        </div>
                      ` : ''}
                      
                      <div class="alternative-product-info">
                        <h4 class="alternative-product-title">${product.title}</h4>
                        
                        <div class="alternative-product-match">
                          <span class="alternative-product-match-badge">
                            ✓ Compatible with ${modelNumber}
                          </span>
                        </div>
                        
                        ${displayPrice ? `
                          <p class="alternative-product-price">${displayPrice}</p>
                        ` : ''}
                        
                        <a href="${product.url}" class="alternative-product-button" target="_blank">
                          View Product
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </a>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
            
            <div class="compatibility-result-actions">
              <button type="button" class="compatibility-result-button compatibility-result-button--secondary" onclick="tryAgainCompatibility()">
                Try Another Model
              </button>
              <button type="button" class="compatibility-result-button compatibility-result-button--secondary" onclick="closeCompatibilityResult()">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Add popup styles
   */
  function addPopupStyles() {
    if (document.getElementById('compatibility-result-styles')) return;

    const styles = `
      <style id="compatibility-result-styles">
        .compatibility-result-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.6);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          backdrop-filter: blur(2px);
        }

        .compatibility-result-overlay.show {
          opacity: 1;
        }

        .compatibility-result-popup {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          transform: scale(0.9);
          transition: transform 0.3s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          position: relative;
        }

        .compatibility-result-overlay.show .compatibility-result-popup {
          transform: scale(1);
        }

        .compatibility-result-close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          font-size: 32px;
          cursor: pointer;
          color: #666;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
          z-index: 1;
        }

        .compatibility-result-close:hover {
          background: #f3f4f6;
          color: #333;
        }

        .compatibility-result-content {
          padding: 40px 30px;
          text-align: center;
        }

        .compatibility-result-icon {
          margin-bottom: 20px;
          display: flex;
          justify-content: center;
        }

        .compatibility-result-title {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 28px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 15px 0;
          line-height: 1.3;
        }

        .compatibility-result-message {
          font-family: 'Avenir Next Medium', 'Avenir Next', Arial, sans-serif;
          font-size: 16px;
          line-height: 1.6;
          color: #4b5563;
          margin: 0 0 30px 0;
        }

        .compatibility-result-metafield {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
          text-align: left;
        }

        .compatibility-result-metafield-title {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: #dc2626;
          margin: 0 0 8px 0;
        }

        .compatibility-result-metafield-content {
          font-family: 'Courier New', monospace;
          font-size: 13px;
          color: #7f1d1d;
          background: white;
          padding: 12px;
          border-radius: 4px;
          border: 1px solid #fecaca;
          word-break: break-all;
          line-height: 1.4;
        }

        .compatibility-result-metafield-content code {
          background: none;
          padding: 0;
          font-family: inherit;
        }

        .compatibility-result-models {
          background: #f9fafb;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 30px;
          text-align: left;
        }

        .compatibility-result-models-title {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 15px 0;
        }

        .compatibility-result-models-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .compatibility-result-model-tag {
          display: inline-block;
          padding: 8px 14px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-family: 'Avenir Next Medium', 'Avenir Next', Arial, sans-serif;
          font-size: 14px;
          color: #374151;
          transition: all 0.2s ease;
        }

        .compatibility-result-model-tag.matched {
          background: #d1fae5;
          border-color: #10b981;
          color: #065f46;
          font-weight: 600;
        }

        .compatibility-result-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .compatibility-result-button {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-weight: 600;
          font-size: 14px;
          padding: 14px 28px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          min-width: 160px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .compatibility-result-button--primary {
          background: #000a46;
          color: white;
          border: 2px solid #000a46;
        }

        .compatibility-result-button--primary:hover {
          background: #000830;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 10, 70, 0.3);
        }

        .compatibility-result-button--secondary {
          background: white;
          color: #374151;
          border: 2px solid #d1d5db;
        }

        .compatibility-result-button--secondary:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        @media (max-width: 768px) {
          .compatibility-result-popup {
            width: 95%;
            margin: 20px;
          }

          .compatibility-result-content {
            padding: 30px 20px;
          }

          .compatibility-result-title {
            font-size: 20px;
          }
          .compatibility-result-models-title {
          text-align: center;
          }
          .compatibility-result-message {
            font-size: 14px;
          }

          .compatibility-result-actions {
            flex-direction: column;
          }

          .compatibility-result-button {
            width: 100%;
          }
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', styles);
  }

  /**
   * Add popup event listeners
   */
  function addPopupEventListeners() {
    const overlay = document.querySelector('.compatibility-result-overlay');
    if (overlay) {
      overlay.addEventListener('click', function(e) {
        if (e.target === overlay) {
          closeCompatibilityResult();
        }
      });
    }

    // Escape key to close
    document.addEventListener('keydown', handleEscapeKey);
  }

  /**
   * Handle escape key
   */
  function handleEscapeKey(e) {
    if (e.key === 'Escape') {
      closeCompatibilityResult();
    }
  }

  /**
   * Remove existing popup
   */
  function removeExistingPopup() {
    const existingOverlay = document.querySelector('.compatibility-result-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    document.removeEventListener('keydown', handleEscapeKey);
  }

  /**
   * Close compatibility result popup
   */
  window.closeCompatibilityResult = function() {
    const overlay = document.querySelector('.compatibility-result-overlay');
    if (overlay) {
      overlay.classList.remove('show');
      setTimeout(() => {
        removeExistingPopup();
      }, 300);
    }
  };

  /**
   * Add to cart from compatibility popup
   */
  window.scrollToAddToCart = function() {
    console.log('🔍 Compatibility Checker: Adding to cart...');
    
    // Get the selected variant ID from the product form
    const variantIdInput = document.querySelector('input[name="id"]');
    const variantId = variantIdInput ? variantIdInput.value : null;
    
    if (!variantId) {
      console.error('🔍 Compatibility Checker: No variant ID found');
      alert('Please select a product variant');
      return;
    }
    
    // Get quantity (default to 1)
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
    
    console.log('🔍 Compatibility Checker: Adding to cart:', { variantId, quantity });
    
    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      return response.json();
    })
    .then(data => {
      console.log('🔍 Compatibility Checker: Added to cart successfully:', data);
      
      // Close the compatibility popup
      closeCompatibilityResult();
      
      // Trigger cart refresh event (this will update cart count and open mini cart)
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          scrollToTop: false
        }
      }));
      
      // Show success feedback (optional - theme will handle this via cart popup)
      console.log('🔍 Compatibility Checker: Product added to basket successfully!');
    })
    .catch(error => {
      console.error('🔍 Compatibility Checker: Error adding to cart:', error);
      closeCompatibilityResult();
      alert('Error adding product to basket. Please try again.');
    });
  };

  /**
   * Try again - close popup and focus on input
   */
  window.tryAgainCompatibility = function() {
    closeCompatibilityResult();
    
    // Find and focus the input
    const input = document.querySelector('.compatibility-checker__input');
    if (input) {
      input.focus();
      input.select();
    }
  };

  /**
   * Show simple message
   */
  function showMessage(message, type) {
    // Simple alert for now - can be enhanced with toast notifications
    alert(message);
  }

  /**
   * Open model number help popup with image
   */
  window.openModelNumberHelpPopup = function(blockId) {
    console.log('🔍 Opening model number help popup for block:', blockId);
    
    // Get the data element
    const dataElement = document.getElementById(`compatibility-data-${blockId}`);
    if (!dataElement) {
      console.warn('🔍 Data element not found for block:', blockId);
      return;
    }
    
    const helpImage = dataElement.getAttribute('data-help-image');
    const helpTitle = dataElement.getAttribute('data-help-title') || 'Where is my refrigerator model number?';
    
    if (!helpImage) {
      console.warn('🔍 No help image configured');
      alert('Help image not configured. Please contact support.');
      return;
    }
    
    // Remove existing help popup if any
    removeExistingHelpPopup();
    
    // Create popup HTML
    const popupHTML = `
      <div class="model-help-popup-overlay">
        <div class="model-help-popup">
          <button type="button" class="model-help-popup__close" onclick="closeModelNumberHelpPopup()">&times;</button>
          
          <div class="model-help-popup__content">
            <h2 class="model-help-popup__title">Where to Find Your Model Number</h2>
            
            <p class="model-help-popup__intro-text">
              You'll usually find your model number on a sticker inside the fridge or freezer. Common spots include:
            </p>
            
            <div class="model-help-popup__image-wrapper">
              <img src="${helpImage}" alt="Where to Find Your Model Number" class="model-help-popup__image">
            </div>
            
            <p class="model-help-popup__footer-text">
              Still can't find it? <a href="/pages/contact-us" class="model-help-popup__contact-link">Get in touch</a>
            </p>
          </div>
        </div>
      </div>
    `;
    
    // Add popup to DOM
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    
    // Add styles if not already added
    addHelpPopupStyles();
    
    // Show popup with animation
    setTimeout(() => {
      const overlay = document.querySelector('.model-help-popup-overlay');
      if (overlay) {
        overlay.classList.add('show');
      }
    }, 10);
    
    // Add event listeners
    addHelpPopupEventListeners();
  };

  /**
   * Close model number help popup
   */
  window.closeModelNumberHelpPopup = function() {
    const overlay = document.querySelector('.model-help-popup-overlay');
    if (overlay) {
      overlay.classList.remove('show');
      setTimeout(() => {
        removeExistingHelpPopup();
      }, 300);
    }
  };

  /**
   * Remove existing help popup
   */
  function removeExistingHelpPopup() {
    const existingOverlay = document.querySelector('.model-help-popup-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    document.removeEventListener('keydown', handleHelpPopupEscapeKey);
  }

  /**
   * Add help popup styles
   */
  function addHelpPopupStyles() {
    if (document.getElementById('model-help-popup-styles')) return;

    const styles = `
      <style id="model-help-popup-styles">
        .model-help-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.75);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          backdrop-filter: blur(3px);
          padding: 20px;
        }

        .model-help-popup-overlay.show {
          opacity: 1;
        }

        .model-help-popup {
          background: white;
          border-radius: 12px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          transform: scale(0.9);
          transition: transform 0.3s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          position: relative;
        }

        .model-help-popup-overlay.show .model-help-popup {
          transform: scale(1);
        }

        .model-help-popup__close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: white;
          border: none;
          font-size: 32px;
          cursor: pointer;
          color: #666;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
          z-index: 10;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .model-help-popup__close:hover {
          background: #f3f4f6;
          color: #333;
          transform: rotate(90deg);
        }

        .model-help-popup__content {
          padding: 40px 30px;
        }

        .model-help-popup__title {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 20px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 15px 0;
          line-height: 1.3;
          text-align: center;
        }

        .model-help-popup__intro-text {
          font-family: 'Avenir Next Medium', 'Avenir Next', Arial, sans-serif;
          font-size: 14px;
          color: #4b5563;
          margin: 0 0 20px 0;
          line-height: 1.5;
          text-align: center;
        }

        .model-help-popup__image-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 15px;
          margin: 0 0 20px 0;
        }

        .model-help-popup__image {
          max-width: 100%;
          height: auto;
          display: block;
          border-radius: 4px;
        }

        .model-help-popup__footer-text {
          font-family: 'Avenir Next Medium', 'Avenir Next', Arial, sans-serif;
          font-size: 14px;
          color: #4b5563;
          margin: 0;
          line-height: 1.5;
          text-align: center;
        }

        .model-help-popup__contact-link {
          color: #1d5787;
          text-decoration: underline;
          font-weight: 500;
        }

        .model-help-popup__contact-link:hover {
          color: #1a4c73;
          text-decoration: none;
        }

        @media (max-width: 768px) {
          .model-help-popup {
            width: 95%;
            margin: 20px;
            max-height: 85vh;
          }

          .model-help-popup__content {
            padding: 30px 20px;
          }

          .model-help-popup__title {
            font-size: 20px;
            margin-bottom: 20px;
          }

          .model-help-popup__close {
            top: 10px;
            right: 10px;
            width: 35px;
            height: 35px;
            font-size: 28px;
          }
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', styles);
  }

  /**
   * Add help popup event listeners
   */
  function addHelpPopupEventListeners() {
    const overlay = document.querySelector('.model-help-popup-overlay');
    if (overlay) {
      overlay.addEventListener('click', function(e) {
        if (e.target === overlay) {
          closeModelNumberHelpPopup();
        }
      });
    }

    // Escape key to close
    document.addEventListener('keydown', handleHelpPopupEscapeKey);
  }

  /**
   * Handle escape key for help popup
   */
  function handleHelpPopupEscapeKey(e) {
    if (e.key === 'Escape') {
      closeModelNumberHelpPopup();
    }
  }

  /**
   * Add alternative products styles
   */
  function addAlternativeProductsStyles() {
    if (document.getElementById('alternative-products-styles')) return;

    const styles = `
      <style id="alternative-products-styles">
        .compatibility-result-popup--large {
          max-width: 900px;
        }

        .alternative-products-section {
          background: #f9fafb;
          border-radius: 12px;
          padding: 25px;
          margin: 25px 0;
        }

        .alternative-products-title {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 20px 0;
          text-align: center;
        }

        .alternative-products-grid {
          display: flex;
          justify-content:center;
          align-items:center;
          gap: 20px;
          max-height: 500px;
          overflow-y: auto;
          padding: 10px;
        }

        .alternative-product-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .alternative-product-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
          transform: translateY(-2px);
        }

        .alternative-product-image {
          width: 100%;
          height: 200px;
          overflow: hidden;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .alternative-product-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 15px;
        }

        .alternative-product-info {
          padding: 15px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .alternative-product-title {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 12px 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .alternative-product-match {
          margin-bottom: 12px;
        }

        .alternative-product-match-badge {
          display: inline-block;
          padding: 6px 12px;
          background: #d1fae5;
          border: 1px solid #10b981;
          border-radius: 6px;
          font-family: 'Avenir Next Medium', 'Avenir Next', Arial, sans-serif;
          font-size: 12px;
          color: #065f46;
          font-weight: 600;
        }

        .alternative-product-price {
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: #000a46;
          margin: 0 0 15px 0;
        }

        .alternative-product-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: #000a46;
          color: white;
          border: none;
          border-radius: 8px;
          font-family: 'Avenir Next Demi', 'Avenir Next', Arial, sans-serif;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: auto;
        }

        .alternative-product-button:hover {
          background: #000830;
          transform: translateX(2px);
          box-shadow: 0 4px 12px rgba(0, 10, 70, 0.3);
        }

        .alternative-product-button svg {
          transition: transform 0.3s ease;
        }

        .alternative-product-button:hover svg {
          transform: translateX(4px);
        }

        @media (max-width: 768px) {
          .compatibility-result-popup--large {
            max-width: 95%;
          }

          .alternative-products-section {
            padding: 15px;
          }

          .alternative-products-grid {
            grid-template-columns: 1fr;
            max-height: 400px;
          }

          .alternative-product-image {
            height: 180px;
          }

          .alternative-products-title {
            font-size: 16px;
          }
        }

        /* Scrollbar styling for the products grid */
        .alternative-products-grid::-webkit-scrollbar {
          width: 8px;
        }

        .alternative-products-grid::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .alternative-products-grid::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .alternative-products-grid::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', styles);
  }

  /**
   * Initialize all compatibility checkers on the page
   */
  function init() {
    console.log('🔍 Product Compatibility Checker: Initializing all checkers...');
    
    // Find all compatibility checker blocks
    const compatibilityBlocks = document.querySelectorAll('[data-compatibility-block-id]');
    
    console.log('🔍 Found compatibility checker blocks:', compatibilityBlocks.length);
    
    compatibilityBlocks.forEach((block) => {
      const blockId = block.getAttribute('data-compatibility-block-id');
      console.log('🔍 Initializing compatibility checker for block:', blockId);
      initCompatibilityChecker(blockId);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  console.log('🔍 Product Compatibility Checker: Script loaded!');

})();

